import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiClock, FiCheck, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';
import ManageHeader from './ManageHeader';
import DeltaPaymentMethods from './DeltaPaymentMethods';
import { useDeltaPaymentReturn } from './deltaPayment';
import { validateDateAgainstOpeningHours, validateDateAgainstClosingPeriods } from './editFlowUtils';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

export default function ExtendFlow({ token, bookingData, refreshBooking }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();

  const getDurationLabel = (option) => {
    const type = option.durationType;
    const val = option.durationValue;
    if (type === 'hours') return t('duration.hours', { count: val });
    if (type === 'calendar_day' || type === 'multi_day' || type === 'days') return t('duration.days', { count: val });
    if (type === 'week' || type === 'weeks') return t('duration.weeks', { count: val });
    return option.durationLabel;
  };
  const motion = useMotionConfig();

  const { booking, merchant, items, location, closingPeriods } = bookingData;

  const [step, setStep] = useState('select');
  const [options, setOptions] = useState([]);
  const [remainingOptions, setRemainingOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [blockedReason, setBlockedReason] = useState(null); // 'EXTEND_BEFORE_START' etc.
  const [extending, setExtending] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [confirmedEndDate, setConfirmedEndDate] = useState(null); // authoritative new end from the server, for the success screen
  const [savedCard, setSavedCard] = useState(null); // { brand, last4 } when a reusable card is on file

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchOptions = async () => {
      try {
        const res = await fetch(`${API_BASE}/manage/extension-options`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // Pre-start access lands here when the user deep-links /extend
          // before pick-up — route them to the edit flow rather than showing
          // a generic error banner alongside the misleading empty state.
          if (err.code === 'EXTEND_BEFORE_START') {
            setBlockedReason('EXTEND_BEFORE_START');
            return;
          }
          throw new Error(err.error || 'Failed to load extension options');
        }
        const data = await res.json();
        setSavedCard(data.savedCard || null);
        const durationToHours = (o) => {
          const val = Number(o.durationValue);
          if (o.durationType === 'hours') return val;
          if (o.durationType === 'calendar_day' || o.durationType === 'multi_day' || o.durationType === 'days') return val * 24;
          if (o.durationType === 'week' || o.durationType === 'weeks') return val * 168;
          return val;
        };
        const sorted = (data.options || []).sort((a, b) => durationToHours(a) - durationToHours(b));

        const bookingItems = items || [];
        const groupedItems = {};
        bookingItems.forEach(i => {
          const key = `${i.product_type_id}_${i.size || ''}`;
          if (!groupedItems[key]) {
            groupedItems[key] = { productTypeId: i.product_type_id, size: i.size || null, needed: 0 };
          }
          groupedItems[key].needed += i.quantity;
        });
        const groups = Object.values(groupedItems);

        const INITIAL_COUNT = 3;
        const initialBatch = sorted.slice(0, INITIAL_COUNT);
        const rest = sorted.slice(INITIAL_COUNT);

        // Check all initial options in parallel (each option's product groups also in parallel)
        const startIso = new Date(booking.start_date).toISOString();
        const initialResults = await Promise.all(initialBatch.map(async (option) => {
          const end = new Date(booking.end_date);
          const val = Number(option.durationValue);
          const type = option.durationType;
          if (type === 'hours') end.setHours(end.getHours() + val);
          else if (type === 'calendar_day' || type === 'multi_day' || type === 'days') end.setDate(end.getDate() + val);
          else if (type === 'week' || type === 'weeks') end.setDate(end.getDate() + val * 7);

          const endIso = end.toISOString();
          try {
            const groupResults = await Promise.all(groups.map(async (group) => {
              const params = new URLSearchParams({
                locationId: booking.location_id,
                startDate: startIso,
                endDate: endIso,
                productTypeId: group.productTypeId,
                excludeBookingId: booking.id
              });
              if (group.size) params.set('size', group.size);
              const availRes = await fetch(`${API_BASE}/availability?${params}`);
              if (!availRes.ok) return false;
              const availData = await availRes.json();
              const avail = (availData.availability || []).reduce((sum, entry) => sum + (entry.available_count || 0), 0);
              return avail >= group.needed;
            }));
            return groupResults.every(Boolean) ? option : null;
          } catch {
            return null;
          }
        }));

        const available = initialResults.filter(Boolean);
        setOptions(available);

        // Only offer "show more" if all initial options were available
        // (if any failed, longer durations are likely unavailable too)
        if (available.length === initialBatch.length && rest.length > 0) {
          setRemainingOptions(rest);
        } else {
          setRemainingOptions([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [token]);

  const handleSelectOption = (option) => {
    setError(null);
    setSelectedOption(option);

    const end = computeNewEnd(option);
    if (end) {
      const endIso = end.toISOString();
      const hoursError = validateDateAgainstOpeningHours(endIso, location?.opening_hours, merchantTz);
      if (hoursError) {
        const label = t('manage.newReturnDate', 'New return date');
        const msg = hoursError.type === 'closed'
          ? t('manage.closedOnDay', 'Location is closed on this day')
          : t('manage.outsideOpeningHours', 'Outside opening hours ({{open}} – {{close}})', { open: hoursError.open, close: hoursError.close });
        setError(`${label}: ${msg}`);
        return;
      }
      const closingError = validateDateAgainstClosingPeriods(booking.start_date, endIso, closingPeriods, merchantTz);
      if (closingError) {
        const reason = closingError.reason ? ` (${closingError.reason})` : '';
        setError(t('manage.closingPeriodOverlap', 'Rental period overlaps with a closing period') + reason);
        return;
      }
    }

    setStep('review');
  };

  const handleShowMore = async () => {
    setLoadingMore(true);
    const bookingItems = items || [];
    const groupedItems = {};
    bookingItems.forEach(i => {
      const key = `${i.product_type_id}_${i.size || ''}`;
      if (!groupedItems[key]) {
        groupedItems[key] = { productTypeId: i.product_type_id, size: i.size || null, needed: 0 };
      }
      groupedItems[key].needed += i.quantity;
    });
    const groups = Object.values(groupedItems);
    const startIso = new Date(booking.start_date).toISOString();

    const moreAvailable = [];
    for (const option of remainingOptions) {
      const end = new Date(booking.end_date);
      const val = Number(option.durationValue);
      const type = option.durationType;
      if (type === 'hours') end.setHours(end.getHours() + val);
      else if (type === 'calendar_day' || type === 'multi_day' || type === 'days') end.setDate(end.getDate() + val);
      else if (type === 'week' || type === 'weeks') end.setDate(end.getDate() + val * 7);

      const endIso = end.toISOString();
      try {
        const groupResults = await Promise.all(groups.map(async (group) => {
          const params = new URLSearchParams({
            locationId: booking.location_id,
            startDate: startIso,
            endDate: endIso,
            productTypeId: group.productTypeId,
            excludeBookingId: booking.id
          });
          if (group.size) params.set('size', group.size);
          const availRes = await fetch(`${API_BASE}/availability?${params}`);
          if (!availRes.ok) return false;
          const availData = await availRes.json();
          const avail = (availData.availability || []).reduce((sum, entry) => sum + (entry.available_count || 0), 0);
          return avail >= group.needed;
        }));
        if (groupResults.every(Boolean)) {
          moreAvailable.push(option);
        } else {
          break;
        }
      } catch {
        break;
      }
    }

    setOptions(prev => [...prev, ...moreAvailable]);
    setRemainingOptions([]);
    setLoadingMore(false);
  };

  // Confirm the Stripe payment server-side, then write the extension. Shared by
  // the inline success path (onPaid) and the redirect-return path. extend-booking
  // fires the Make.com notification, so confirm-delta-payment is not asked to.
  const finalizeExtension = async (paymentIntentId, ctx = {}, { skipConfirm = false } = {}) => {
    if (!skipConfirm && paymentIntentId) {
      try {
        await fetch(`${API_BASE}/manage/confirm-delta-payment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId })
        });
      } catch (err) {
        console.warn('[EXTEND] Failed to confirm payment server-side:', err.message);
      }
    }
    const ok = await executeExtension(paymentIntentId, ctx.option);
    if (!ok) { setFinalizing(false); setStep('select'); }
    return ok;
  };

  // Finalize an extension paid via an off-site redirect (MobilePay, Pay-by-Bank).
  useDeltaPaymentReturn({
    onFinalize: finalizeExtension,
    onProcessing: () => setFinalizing(true),
    onError: (msg) => { setError(msg); setFinalizing(false); setStep('select'); },
  });

  const computeNewEnd = (opt) => {
    const option = opt || selectedOption;
    if (!option || !booking.end_date) return null;

    // Prefer the server's authoritative value. extension-options derives it with
    // computeExtensionEnd, which snaps day/week extensions to the location's
    // closing time in the merchant's timezone. The local math below does neither,
    // so validating it would check a different moment than extend-booking
    // persists — and extend-booking now rejects a return outside opening hours,
    // which would surface as a failure after payment rather than at selection.
    if (option.newEndDate) {
      const fromServer = new Date(option.newEndDate);
      if (!isNaN(fromServer.getTime())) return fromServer;
    }

    const end = new Date(booking.end_date);
    const val = Number(option.durationValue);
    const type = option.durationType;
    if (type === 'hours') end.setHours(end.getHours() + val);
    else if (type === 'calendar_day' || type === 'multi_day' || type === 'days') end.setDate(end.getDate() + val);
    else if (type === 'week' || type === 'weeks') end.setDate(end.getDate() + val * 7);
    return end;
  };

  // Collapse the booking's items into per (product_type, size) demand — the unit
  // the availability endpoint aggregates on. Shared by the load-time filter and
  // the pre-payment re-check below.
  const buildGroups = () => {
    const grouped = {};
    (items || []).forEach(i => {
      const key = `${i.product_type_id}_${i.size || ''}`;
      if (!grouped[key]) grouped[key] = { productTypeId: i.product_type_id, size: i.size || null, needed: 0 };
      grouped[key].needed += i.quantity;
    });
    return Object.values(grouped);
  };

  // Re-verify a single option is still bookable RIGHT NOW, over its full extended
  // window with this booking's own units freed. Used just before we charge, so a
  // slot that filled while the customer sat on the review screen is caught before
  // any payment is taken. The server (extend-booking) re-checks authoritatively
  // and refunds if the window fills during payment; this is the no-charge fast path.
  const checkOptionAvailable = async (option) => {
    // Prefer the server's authoritative new end (timezone-correct, includes the
    // closing-time snap) so the pre-check window matches what will be persisted.
    const end = displayEnd(option);
    if (!end) return true;
    const startIso = new Date(booking.start_date).toISOString();
    const endIso = end.toISOString();
    try {
      const results = await Promise.all(buildGroups().map(async (group) => {
        const params = new URLSearchParams({
          locationId: booking.location_id,
          startDate: startIso,
          endDate: endIso,
          productTypeId: group.productTypeId,
          excludeBookingId: booking.id
        });
        if (group.size) params.set('size', group.size);
        const availRes = await fetch(`${API_BASE}/availability?${params}`);
        if (!availRes.ok) return false;
        const availData = await availRes.json();
        const avail = (availData.availability || []).reduce((sum, entry) => sum + (entry.available_count || 0), 0);
        return avail >= group.needed;
      }));
      return results.every(Boolean);
    } catch {
      return false;
    }
  };

  const handleConfirm = async () => {
    if (!selectedOption) return;
    setError(null);

    // Pre-payment availability gate: don't take a payment for an extension that
    // just became unavailable. On conflict, drop the now-unavailable option and
    // return to the list rather than proceeding to charge.
    setExtending(true);
    const stillAvailable = await checkOptionAvailable(selectedOption);
    setExtending(false);
    if (!stillAvailable) {
      setError(t('manage.extensionNoLongerAvailable', 'This extension is no longer available — the item has just been booked for that period. Please choose a shorter extension.'));
      setOptions(prev => prev.filter(o => o.key !== selectedOption.key));
      setRemainingOptions([]);
      setSelectedOption(null);
      setStep('select');
      return;
    }

    if (selectedOption.totalPrice > 0 && booking.payment_state !== 'open_pay_later') {
      setStep('payment');
    } else {
      await executeExtension();
    }
  };

  const executeExtension = async (paymentIntentId = null, option = selectedOption) => {
    if (!option) {
      setError(t('manage.somethingWrong', 'Something went wrong. Please try again.'));
      return false;
    }
    setExtending(true);
    try {
      const res = await fetch(`${API_BASE}/manage/extend-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          durationType: option.durationType,
          durationValue: option.durationValue,
          totalPrice: option.totalPrice,
          paymentIntentId,
          // Don't send updatedAt after a paid flow — the webhook will have
          // changed it between page load and now, causing a false 409 conflict.
          updatedAt: paymentIntentId ? undefined : booking.updated_at
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to extend booking');
      }

      // Capture the server's authoritative new end for the confirmation screen
      // (selectedOption is gone after a redirect-based payment, so we can't
      // recompute it client-side there).
      const data = await res.json().catch(() => ({}));
      if (data?.newEndDate) setConfirmedEndDate(data.newEndDate);

      setFinalizing(false);
      setStep('success');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setExtending(false);
    }
  };

  const handleDone = async () => {
    await refreshBooking();
    navigate(`/b/${token}`);
  };

  const merchantTz = merchant?.timezone || 'UTC';

  const formatEnd = (date) => date.toLocaleString(undefined, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: merchantTz
  });

  // Prefer the server's authoritative end for the option (matches what
  // extend-booking writes, incl. the closing-time rule); fall back to the client
  // estimate only if an older API didn't supply it.
  const displayEnd = (opt) => {
    const option = opt || selectedOption;
    if (option?.newEndDate) return new Date(option.newEndDate);
    return computeNewEnd(option);
  };

  // Review-step preview.
  const computeNewEndDate = () => {
    const end = displayEnd();
    return end ? formatEnd(end) : '';
  };

  // Confirmation-screen value: prefer the server's authoritative end (survives a
  // redirect payment); fall back to the client estimate for the inline path.
  const confirmedEndLabel = confirmedEndDate
    ? formatEnd(new Date(confirmedEndDate))
    : computeNewEndDate();

  // Redirect-based payment (MobilePay, Pay-by-Bank) is being finalized on return.
  if (finalizing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 flex justify-center">
          <div role="status" className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="text-sm text-gray-600">{t('manage.processingPayment', 'Processing payment...')}</span>
          </div>
        </main>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${theme.colors.primary.base}15` }}
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10" style={{ color: theme.colors.primary.base }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.extendSuccess', 'Rental Extended!')}
            </h1>
            <p className="text-gray-500 mb-2">
              {t('manage.extendSuccessDesc', 'Your rental has been successfully extended.')}
            </p>
            {confirmedEndLabel && (
              <p className="text-sm text-gray-600 mb-8">
                {t('manage.newReturnDate', 'New return date')}: <span className="font-semibold">{confirmedEndLabel}</span>
              </p>
            )}
            <button
              onClick={handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ManageHeader merchant={merchant} token={token} showBack backLabel={t('manage.backToBooking', 'Back')} />

      <main id="main-content" className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <m.div {...motion.fadeInUp}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('manage.extendRental', 'Extend Rental')}
          </h1>
          <p className="text-gray-500 text-sm">
            {t('manage.extendDesc', 'Choose how long you would like to extend your rental.')}
          </p>
        </m.div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {step === 'select' && blockedReason === 'EXTEND_BEFORE_START' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4 text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('manage.extendBeforeStartTitle', 'Use Edit Booking before pick-up')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('manage.extendBeforeStartBody', 'Changes to the rental period before pick-up go through Edit Booking. The Extend flow becomes available once the rental has started.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => navigate(`/b/${token}`)}
                className="flex-1 py-3 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('manage.backToBooking', 'Back to Booking')}
              </button>
              <button
                onClick={() => navigate(`/b/${token}/edit`)}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-colors"
                style={{ backgroundColor: theme.colors.primary.base }}
              >
                {t('manage.editBooking', 'Edit Booking')}
              </button>
            </div>
          </div>
        )}

        {step === 'select' && !blockedReason && (
          <>
            {loading ? (
              <div role="status" className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                <span className="sr-only">{t('manage.loading', 'Loading...')}</span>
              </div>
            ) : !error && options.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center space-y-2">
                <p className="text-gray-700 font-medium">{t('manage.noExtensionAvailable', 'Unfortunately, no extension is currently possible for the next available durations.')}</p>
                <p className="text-gray-500 text-sm">{t('manage.returnOnTime', 'Please make sure to return the equipment by the scheduled return date.')}</p>
              </div>
            ) : options.length === 0 ? null : (
              <div className="space-y-3">
                {options.map((option) => {
                  const label = getDurationLabel(option);
                  return (
                    <m.button
                      key={option.key}
                      {...motion.fadeInUp}
                      onClick={() => handleSelectOption(option)}
                      className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-left hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${theme.colors.primary.base}15` }}
                          >
                            <SafeIcon icon={FiClock} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
                          </div>
                          <p className="font-semibold text-gray-900">{label}</p>
                        </div>
                        <p className="text-lg font-bold" style={{ color: theme.colors.primary.base }}>
                          {option.totalPrice > 0 ? formatCurrency(option.totalPrice) : t('manage.free', 'Free')}
                        </p>
                      </div>
                    </m.button>
                  );
                })}
              </div>
            )}

            {remainingOptions.length > 0 && (
              <button
                onClick={handleShowMore}
                disabled={loadingMore}
                className="w-full py-3 rounded-xl font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {loadingMore
                  ? t('manage.checkingAvailability', 'Checking availability...')
                  : t('manage.showMoreOptions', 'Show more options')}
              </button>
            )}
          </>
        )}

        {step === 'review' && selectedOption && (
          <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('manage.reviewExtension', 'Review Extension')}</h2>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('manage.extensionDuration', 'Duration')}</span>
                <span className="font-medium text-gray-900">
                  {getDurationLabel(selectedOption)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('manage.currentReturnDate', 'Current return')}</span>
                <span className="font-medium text-gray-900">
                  {new Date(booking.end_date).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: merchantTz })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('manage.newReturnDate', 'New return')}</span>
                <span className="font-semibold" style={{ color: theme.colors.primary.base }}>
                  {computeNewEndDate()}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900">{t('manage.extensionPrice', 'Extension price')}</span>
                  <span style={{ color: theme.colors.primary.base }}>
                    {selectedOption.totalPrice > 0 ? formatCurrency(selectedOption.totalPrice) : t('manage.free', 'Free')}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setStep('select'); setSelectedOption(null); setError(null); }}
                className="flex-1 py-3 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('manage.back', 'Back')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={extending}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: theme.colors.primary.base }}
              >
                {extending ? t('manage.processing', 'Processing...') : t('manage.confirm', 'Confirm')}
              </button>
            </div>
          </m.div>
        )}

        {step === 'payment' && selectedOption && (
          <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('manage.payment', 'Payment')}</h2>
            <button
              onClick={() => { setStep('review'); setError(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              {t('manage.back', 'Back')}
            </button>
            <DeltaPaymentMethods
              token={token}
              merchant={merchant}
              booking={booking}
              amount={selectedOption.totalPrice}
              description={`Extension: ${selectedOption.durationLabel}`}
              applyContext={{ option: { durationType: selectedOption.durationType, durationValue: selectedOption.durationValue, totalPrice: selectedOption.totalPrice } }}
              savedCard={savedCard}
              idempotencyPrefix="ext"
              onPaid={(paymentIntentId, opts) => finalizeExtension(
                paymentIntentId,
                { option: { durationType: selectedOption.durationType, durationValue: selectedOption.durationValue, totalPrice: selectedOption.totalPrice } },
                opts
              )}
              onError={(msg) => setError(msg)}
            />
          </m.div>
        )}
      </main>
    </div>
  );
}
